import React, { useEffect, useState } from 'react';
import { Line, Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  Filler
} from 'chart.js';
import { format, subMonths, addMonths } from 'date-fns';
import { Calendar, ChevronLeft, ChevronRight, TrendingUp, DollarSign, BarChart3 } from 'lucide-react';
import apiService from '../services/apiService';
import { CombinedCategory } from '../types';
import { ChartSkeleton } from './SkeletonLoaders';
import { formatCurrency, formatNumber } from '../utils/formatters';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

interface CategoryChartsGridProps {
  currency: string;
  startDate?: Date | null;
  endDate?: Date | null;
  onDateRangeChange?: (startDate: Date, endDate: Date) => void;
}

const CategoryChartsGrid: React.FC<CategoryChartsGridProps> = ({
  currency,
  startDate: propStartDate,
  endDate: propEndDate,
  onDateRangeChange
}) => {
  const [categoriesData, setCategoriesData] = useState<CombinedCategory[]>([]);
  const [loading, setLoading] = useState(true);
  const [chartType, setChartType] = useState<'line' | 'bar'>('line');
  const [activeTab, setActiveTab] = useState<'all' | 'parents' | 'subcategories'>('all');

  // Date selection state for local date picker
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [tempStartMonth, setTempStartMonth] = useState(propStartDate?.getMonth() || 0);
  const [tempStartYear, setTempStartYear] = useState(propStartDate?.getFullYear() || new Date().getFullYear());
  const [tempEndMonth, setTempEndMonth] = useState(propEndDate?.getMonth() || 0);
  const [tempEndYear, setTempEndYear] = useState(propEndDate?.getFullYear() || new Date().getFullYear());

  // Load data effect - dates are optional (undefined = all-time data)
  useEffect(() => {
    let cancelled = false;

    const loadCategoryData = async () => {
      try {
        setLoading(true);

        // Dates are optional - undefined means load all-time data
        const startDateStr = propStartDate ? format(propStartDate, 'yyyy-MM-dd') : undefined;
        const endDateStr = propEndDate ? format(propEndDate, 'yyyy-MM-dd') : undefined;

        const categoryData = await apiService.getCategoryTrends(currency, startDateStr, endDateStr);

        if (cancelled) return;

        setCategoriesData(categoryData.categories || []);
      } catch (error) {
        if (cancelled) return;
        console.error('Error loading category data:', error);
        setCategoriesData([]);
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    loadCategoryData();

    return () => {
      cancelled = true;
    };
  }, [currency, propStartDate, propEndDate]);

  // Date picker functions
  const monthNames = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  const currentYear = new Date().getFullYear();
  const yearOptions = Array.from({ length: 10 }, (_, i) => currentYear - 5 + i);

  const handleDatePickerApply = () => {
    const newStartDate = new Date(tempStartYear, tempStartMonth, 1);
    const newEndDate = new Date(tempEndYear, tempEndMonth + 1, 0);

    // Validate: start date should not be after end date
    if (newStartDate > newEndDate) {
      alert('Start date cannot be after end date');
      return;
    }

    // Notify parent component about date change
    if (onDateRangeChange) {
      onDateRangeChange(newStartDate, newEndDate);
    }
    setShowDatePicker(false);
  };

  const handleDatePickerCancel = () => {
    setTempStartMonth(propStartDate?.getMonth() || 0);
    setTempStartYear(propStartDate?.getFullYear() || new Date().getFullYear());
    setTempEndMonth(propEndDate?.getMonth() || 0);
    setTempEndYear(propEndDate?.getFullYear() || new Date().getFullYear());
    setShowDatePicker(false);
  };

  const handleDatePickerOpen = () => {
    setTempStartMonth(propStartDate?.getMonth() || 0);
    setTempStartYear(propStartDate?.getFullYear() || new Date().getFullYear());
    setTempEndMonth(propEndDate?.getMonth() || 0);
    setTempEndYear(propEndDate?.getFullYear() || new Date().getFullYear());
    setShowDatePicker(true);
  };

  const goToPreviousMonth = () => {
    if (!propStartDate || !propEndDate) return;
    const newStartDate = subMonths(propStartDate, 1);
    const newEndDate = subMonths(propEndDate, 1);
    if (onDateRangeChange) {
      onDateRangeChange(newStartDate, newEndDate);
    }
  };

  const goToNextMonth = () => {
    if (!propStartDate || !propEndDate) return;
    const newStartDate = addMonths(propStartDate, 1);
    const newEndDate = addMonths(propEndDate, 1);
    if (onDateRangeChange) {
      onDateRangeChange(newStartDate, newEndDate);
    }
  };

  // Generate colors for categories - parent and subcategories share color families
  const getColorForCategory = (categoryData: CombinedCategory, index: number) => {
    const baseColors = [
      '#fe3c72', '#45B7D1', '#96CEB4', '#FFEAA7', '#DDA0DD',
      '#98D8C8', '#FDA7DF', '#B8860B', '#20B2AA', '#FF6347',
      '#9370DB', '#3CB371', '#F4A460', '#FFB6C1', '#40E0D0'
    ];

    if (categoryData.is_subcategory && categoryData.parent_category) {
      // Find the parent category in the data to get its color index
      let parentColorIndex = 0;
      let currentParentIndex = 0;

      // Find which parent this subcategory belongs to
      for (const cat of categoriesData) {
        if (!cat.is_subcategory) {
          if (cat.name === categoryData.parent_category) {
            parentColorIndex = currentParentIndex;
            break;
          }
          currentParentIndex++;
        }
      }

      const baseColor = baseColors[parentColorIndex % baseColors.length];

      // Create a lighter variant for subcategory (60% opacity)
      const rgb = hexToRgb(baseColor);
      if (rgb) {
        return `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, 0.6)`;
      }
      return baseColor;
    } else {
      // Parent category gets full color - count only parent categories for color indexing
      let parentIndex = 0;
      for (let i = 0; i < index; i++) {
        if (!categoriesData[i]?.is_subcategory) {
          parentIndex++;
        }
      }
      return baseColors[parentIndex % baseColors.length];
    }
  };

  // Helper function to convert hex to RGB
  const hexToRgb = (hex: string) => {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    return result ? {
      r: parseInt(result[1], 16),
      g: parseInt(result[2], 16),
      b: parseInt(result[3], 16)
    } : null;
  };

  const createChartOptions = (categoryData: CombinedCategory, index: number) => ({
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      title: {
        display: false
      },
      legend: {
        display: false
      },
      tooltip: {
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        titleColor: '#fff',
        bodyColor: '#fff',
        borderColor: getColorForCategory(categoryData, index),
        borderWidth: 1,
        cornerRadius: 8,
        displayColors: false,
        callbacks: {
          title: (context: any) => context[0].label,
          label: (context: any) => formatCurrency(context.parsed.y, currency)
        }
      }
    },
    scales: {
      x: {
        display: true,
        ticks: {
          font: { size: 9 },
          color: 'var(--text-tertiary)',
          maxRotation: 0,
          callback: function(_: any, i: number) {
            const labels = categoryData.monthly_data?.map(d => d.label) || [];
            const label = labels[i] || '';
            // Show short month: "Jan", "Feb", etc.
            return label.split(' ')[0]?.substring(0, 3) || '';
          },
          autoSkip: true,
          maxTicksLimit: 6,
        },
        grid: { display: false },
        border: { display: false },
      },
      y: {
        display: true,
        beginAtZero: true,
        position: 'left' as const,
        afterFit(scale: any) {
          scale.width += 4;
        },
        ticks: {
          font: { size: 9 },
          color: 'var(--text-tertiary)',
          maxTicksLimit: 4,
          padding: 6,
          callback: function(value: any) {
            if (value >= 1000000) return (value / 1000000).toFixed(1) + 'M';
            if (value >= 1000) return (value / 1000).toFixed(0) + 'k';
            return value;
          },
        },
        grid: {
          color: 'rgba(0,0,0,0.04)',
          drawTicks: false,
        },
        border: { display: false },
      }
    },
    interaction: {
      intersect: false,
      mode: 'index' as const,
    },
    elements: {
      line: {
        borderWidth: 2,
      },
      point: {
        radius: 3,
        hoverRadius: 6
      }
    }
  });

  if (loading) {
    return (
      <div className="category-charts-container">
        <div className="category-charts-header">
          <div className="category-charts-title-section">
            <h2 className="category-charts-title">Category Trends</h2>
            <p className="category-charts-subtitle">Track spending patterns by category over time</p>
          </div>
        </div>
        <div className="category-charts-grid">
          {[...Array(6)].map((_, index) => (
            <div key={index} className="category-chart-card">
              <div className="category-chart-card-header">
                <div className="skeleton" style={{ width: '70%', height: '20px' }} />
                <div className="skeleton" style={{ width: '50%', height: '16px', marginTop: '4px' }} />
              </div>
              <div className="category-chart-wrapper">
                <ChartSkeleton />
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="category-charts-container">
      <div className="category-charts-header">
        <div className="category-charts-title-section">
          <h2 className="category-charts-title">Category Trends</h2>
          <div className="category-charts-controls">
            <button
              onClick={() => setChartType('line')}
              className={`chart-type-btn ${chartType === 'line' ? 'active' : ''}`}
              title="Line Chart"
            >
              <TrendingUp size={16} />
            </button>
            <button
              onClick={() => setChartType('bar')}
              className={`chart-type-btn ${chartType === 'bar' ? 'active' : ''}`}
              title="Bar Chart"
            >
              <BarChart3 size={16} />
            </button>
          </div>
        </div>

        {/* Category Type Tabs */}
        <div className="category-tabs">
          <button
            onClick={() => setActiveTab('all')}
            className={`category-tab ${activeTab === 'all' ? 'active' : ''}`}
          >
            All Categories
          </button>
          <button
            onClick={() => setActiveTab('parents')}
            className={`category-tab ${activeTab === 'parents' ? 'active' : ''}`}
          >
            Parent Categories
          </button>
          <button
            onClick={() => setActiveTab('subcategories')}
            className={`category-tab ${activeTab === 'subcategories' ? 'active' : ''}`}
          >
            Subcategories
          </button>
        </div>

        {/* Date Range Controls */}
        <div className="category-date-controls">
          <button
            onClick={goToPreviousMonth}
            className="btn btn-icon btn-secondary"
            title="Previous month"
          >
            <ChevronLeft size={16} />
          </button>

          <div className="category-current-period" onClick={handleDatePickerOpen} style={{ cursor: 'pointer' }}>
            <Calendar size={14} />
            <span className="category-period-text">
              {propStartDate && propEndDate ? `${format(propStartDate, 'MMM yyyy')} - ${format(propEndDate, 'MMM yyyy')}` : 'All Time'}
            </span>
          </div>

          <button
            onClick={goToNextMonth}
            className="btn btn-icon btn-secondary"
            title="Next month"
          >
            <ChevronRight size={16} />
          </button>
        </div>
      </div>

      {/* Date Picker Dropdown */}
      {showDatePicker && (
        <>
          <div className="datepicker-backdrop" onClick={handleDatePickerCancel}></div>
          <div className="category-datepicker-popup">
            <div className="datepicker-header">
              <div className="datepicker-section">
                <label>Start Date:</label>
                <div className="datepicker-controls">
                  <select
                    className="datepicker-select"
                    value={tempStartMonth}
                    onChange={(e) => setTempStartMonth(parseInt(e.target.value))}
                  >
                    {monthNames.map((month, index) => (
                      <option key={month} value={index}>
                        {month}
                      </option>
                    ))}
                  </select>
                  <select
                    className="datepicker-select"
                    value={tempStartYear}
                    onChange={(e) => setTempStartYear(parseInt(e.target.value))}
                  >
                    {yearOptions.map((year) => (
                      <option key={year} value={year}>
                        {year}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="datepicker-section">
                <label>End Date:</label>
                <div className="datepicker-controls">
                  <select
                    className="datepicker-select"
                    value={tempEndMonth}
                    onChange={(e) => setTempEndMonth(parseInt(e.target.value))}
                  >
                    {monthNames.map((month, index) => (
                      <option key={month} value={index}>
                        {month}
                      </option>
                    ))}
                  </select>
                  <select
                    className="datepicker-select"
                    value={tempEndYear}
                    onChange={(e) => setTempEndYear(parseInt(e.target.value))}
                  >
                    {yearOptions.map((year) => (
                      <option key={year} value={year}>
                        {year}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>

            <div className="datepicker-footer">
              <button
                type="button"
                onClick={handleDatePickerCancel}
                className="datepicker-btn datepicker-btn-cancel"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleDatePickerApply}
                className="datepicker-btn datepicker-btn-apply"
              >
                Apply
              </button>
            </div>
          </div>
        </>
      )}

      {/* Category Charts Grid */}
      {categoriesData.length === 0 ? (
        <div className="category-charts-empty">
          <p>No category data found for the selected period.</p>
        </div>
      ) : (
        <div className="category-charts-grid">
          {categoriesData
            .filter(categoryData => {
              if (activeTab === 'all') return true;
              if (activeTab === 'parents') return !categoryData.is_subcategory;
              if (activeTab === 'subcategories') return categoryData.is_subcategory;
              return true;
            })
            .sort((a, b) => b.total - a.total)
            .map((categoryData, index) => {
            const color = getColorForCategory(categoryData, index);

            // Helper function to create background color with proper transparency
            const getBackgroundColor = (color: string, chartType: 'line' | 'bar') => {
              if (chartType === 'bar') return color;

              // For line charts, create a transparent version
              if (color.startsWith('rgba')) {
                // If already rgba, modify the alpha value
                return color.replace(/[\d.]+\)$/g, '0.1)');
              } else if (color.startsWith('#')) {
                // Convert hex to rgba with transparency
                const hex = color.replace('#', '');
                const r = parseInt(hex.substr(0, 2), 16);
                const g = parseInt(hex.substr(2, 2), 16);
                const b = parseInt(hex.substr(4, 2), 16);
                return `rgba(${r}, ${g}, ${b}, 0.1)`;
              }
              return `${color}20`; // fallback
            };

            const chartData = {
              labels: categoryData.monthly_data?.map(d => d.label) || [],
              datasets: [
                {
                  label: categoryData.name,
                  data: categoryData.monthly_data?.map(d => d.total) || [],
                  borderColor: color,
                  backgroundColor: getBackgroundColor(color, chartType),
                  fill: chartType === 'line',
                  tension: chartType === 'line' ? 0.4 : 0,
                  pointBackgroundColor: color,
                  pointBorderColor: '#fff',
                  pointBorderWidth: 2,
                  pointRadius: chartType === 'line' ? 4 : 0,
                  pointHoverRadius: 6,
                }
              ]
            };

            return (
              <div
                key={categoryData.name}
                className={`category-chart-card ${categoryData.is_subcategory ? 'subcategory-card' : 'parent-category-card'}`}
                style={categoryData.is_subcategory ? { borderLeft: `4px solid ${color}` } : {}}
              >
                <div className="category-card-header">
                  <div className="category-card-title">
                    <div className="category-color-dot" style={{ backgroundColor: color }}></div>
                    <div className="category-name-container">
                      <h4 className={`category-name ${categoryData.is_subcategory ? 'subcategory-name' : ''}`}>
                        {categoryData.is_subcategory ? categoryData.name.split(' › ')[1] : categoryData.name}
                      </h4>
                      {categoryData.is_subcategory && categoryData.parent_category && (
                        <span className="parent-category-label">
                          {categoryData.parent_category}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className={`category-trend-indicator trend-${categoryData.trend}`}>
                    <TrendingUp size={14} />
                  </div>
                </div>

                <div className="category-chart-wrapper">
                  {chartType === 'line' ? (
                    <Line data={chartData} options={createChartOptions(categoryData, index)} />
                  ) : (
                    <Bar data={chartData} options={createChartOptions(categoryData, index)} />
                  )}
                </div>

                <div className="category-card-stats">
                  <div className="category-stat">
                    <div className="category-stat-icon">
                      <DollarSign size={12} />
                    </div>
                    <div className="category-stat-content">
                      <span className="category-stat-label">Total</span>
                      <span className="category-stat-value">{formatNumber(categoryData.total)}</span>
                    </div>
                  </div>
                  <div className="category-stat">
                    <div className="category-stat-content">
                      <span className="category-stat-label">Avg</span>
                      <span className="category-stat-value">{formatNumber(categoryData.average)}</span>
                    </div>
                  </div>
                  <div className="category-stat">
                    <div className="category-stat-content">
                      <span className="category-stat-label">Peak</span>
                      <span className="category-stat-value">{formatNumber(categoryData.highest)}</span>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default CategoryChartsGrid;
